import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';

// Icons
const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <line x1="19" y1="8" x2="19" y2="14" />
    <line x1="22" y1="11" x2="16" y2="11" />
  </svg>
);

const ManageFollowing = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme, isDark } = useTheme();
  const navigate = useNavigate();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSelectConfirm, setShowSelectConfirm] = useState(false);
  const [unfollowing, setUnfollowing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Add user modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [followingUser, setFollowingUser] = useState(null);

  // Set currentUserId from activeProfile (supports child profile when parent views as child)
  useEffect(() => {
    if (activeProfile?.id) {
      setCurrentUserId(activeProfile.id);
    }
  }, [activeProfile?.id]);

  // Fetch following list
  useEffect(() => {
    const fetchFollowing = async () => {
      if (!currentUserId) return;

      try {
        const { data, error } = await supabase
          .from('user_follows')
          .select(`
            id,
            following_id,
            created_at,
            users:following_id (
              id,
              username,
              avatar_url,
              location_city,
              location_state,
              trade_level
            )
          `)
          .eq('follower_id', currentUserId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setFollowing(data || []);
      } catch (err) {
        console.error('Error fetching following:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowing();
  }, [currentUserId]);

  // Filter following by search query
  const filteredFollowing = following.filter(f => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const username = f.users?.username?.toLowerCase() || '';
    const city = f.users?.location_city?.toLowerCase() || '';
    return username.includes(query) || city.includes(query);
  });

  // Handle entering select mode
  const handleEnterSelectMode = () => {
    setShowSelectConfirm(true);
  };

  // Confirm entering select mode
  const confirmSelectMode = () => {
    setShowSelectConfirm(false);
    setSelectMode(true);
    setSelectedUsers(new Set());
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    if (!selectMode) return;
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Handle unfollow selected
  const handleUnfollowSelected = () => {
    if (selectedUsers.size === 0) return;
    setShowConfirmModal(true);
  };

  // Confirm unfollow
  const confirmUnfollow = async () => {
    if (!currentUserId || selectedUsers.size === 0) return;

    setUnfollowing(true);
    try {
      const selectedArray = Array.from(selectedUsers);

      // Delete all selected follows
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', currentUserId)
        .in('following_id', selectedArray);

      if (error) throw error;

      // Update local state
      setFollowing(prev => prev.filter(f => !selectedUsers.has(f.users?.id)));
      setSelectedUsers(new Set());
      setSelectMode(false);
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error unfollowing:', err);
      alert('Failed to unfollow. Please try again.');
    } finally {
      setUnfollowing(false);
    }
  };

  // Cancel select mode
  const cancelSelectMode = () => {
    setSelectMode(false);
    setSelectedUsers(new Set());
  };

  // Search for users to follow
  const handleSearchUsers = async () => {
    if (!addSearchQuery.trim() || !currentUserId) return;

    setSearchLoading(true);
    try {
      // Get list of users already being followed
      const followingIds = following.map(f => f.users?.id).filter(Boolean);

      // Search for users by username, excluding:
      // - Current user
      // - Users already being followed
      // - Users with hide_profile = true
      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url, location_city, location_state, trade_level')
        .ilike('username', `%${addSearchQuery.trim()}%`)
        .neq('id', currentUserId)
        .not('id', 'in', `(${followingIds.length > 0 ? followingIds.join(',') : '0'})`)
        .or('hide_profile.is.null,hide_profile.eq.false')
        .limit(20);

      if (error) throw error;

      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Follow a user
  const handleFollowUser = async (userToFollow) => {
    if (!currentUserId) return;

    setFollowingUser(userToFollow.id);
    try {
      const { error } = await supabase
        .from('user_follows')
        .insert({
          follower_id: currentUserId,
          following_id: userToFollow.id,
        });

      if (error) throw error;

      // Add to local following list
      setFollowing(prev => [{
        id: Date.now(), // Temp ID
        following_id: userToFollow.id,
        created_at: new Date().toISOString(),
        users: userToFollow,
      }, ...prev]);

      // Remove from search results
      setSearchResults(prev => prev.filter(u => u.id !== userToFollow.id));
    } catch (err) {
      console.error('Error following user:', err);
      alert('Failed to follow user. Please try again.');
    } finally {
      setFollowingUser(null);
    }
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setAddSearchQuery('');
    setSearchResults([]);
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffMonths === 1) return '1 month ago';
    if (diffMonths < 12) return `${diffMonths} months ago`;
    return date.toLocaleDateString();
  };

  const styles = {
    container: {
      height: '100%',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      backgroundColor: theme.bg,
      flexShrink: 0,
      position: 'relative',
      gap: '12px',
    },
    backButton: {
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: theme.text,
    },
    headerTitle: {
      color: theme.text,
      fontSize: '20px',
      fontWeight: '700',
      margin: 0,
      flex: 1,
      textAlign: 'center',
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    addButton: {
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: theme.accent,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: theme.text,
    },
    removeButton: {
      padding: '8px 14px',
      backgroundColor: 'transparent',
      border: `1px solid ${theme.error}`,
      borderRadius: '8px',
      color: theme.error,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
    },
    cancelSelectButton: {
      padding: '8px 16px',
      backgroundColor: 'transparent',
      border: `1px solid ${theme.textSecondary}`,
      borderRadius: '8px',
      color: theme.textSecondary,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
    },
    headerBorder: {
      position: 'absolute',
      bottom: 0,
      left: '40px',
      right: '40px',
      height: '2px',
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
      borderRadius: '1px',
    },
    searchContainer: {
      padding: '12px 16px',
      backgroundColor: theme.bg,
    },
    searchInputWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      color: theme.textSecondary,
    },
    searchInput: {
      flex: 1,
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      color: theme.text,
      fontSize: '14px',
    },
    clearSearch: {
      backgroundColor: 'transparent',
      border: 'none',
      color: theme.textSecondary,
      cursor: 'pointer',
      padding: 0,
      display: 'flex',
    },
    selectionBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      backgroundColor: theme.bgSheet,
      borderBottom: `1px solid ${theme.border}`,
    },
    selectionText: {
      color: '#8a8a8a',
      fontSize: '14px',
    },
    unfollowButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: `1px solid ${theme.error}`,
      borderRadius: '10px',
      color: theme.error,
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: '0 16px 100px',
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: `3px solid ${theme.border}`,
      borderTopColor: theme.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: theme.textSecondary,
    },
    emptyTitle: {
      color: theme.text,
      fontSize: '16px',
      fontWeight: '600',
      margin: '16px 0 8px 0',
    },
    emptySubtext: {
      color: theme.textSecondary,
      fontSize: '14px',
      margin: 0,
    },
    userCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '14px 16px',
      backgroundColor: theme.bgSheet,
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      marginTop: '10px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    userCardSelected: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: theme.error,
    },
    checkbox: {
      width: '24px',
      height: '24px',
      borderRadius: '6px',
      border: '2px solid #4a4a4a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    checkboxChecked: {
      backgroundColor: theme.error,
      borderColor: theme.error,
      color: theme.text,
    },
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: theme.border,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      overflow: 'hidden',
    },
    avatarImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    avatarInitial: {
      color: theme.accent,
      fontSize: '18px',
      fontWeight: '600',
    },
    userInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minWidth: 0,
    },
    username: {
      color: theme.text,
      fontSize: '15px',
      fontWeight: '600',
    },
    userMeta: {
      color: theme.textSecondary,
      fontSize: '12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    tradeLevel: {
      padding: '4px 10px',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      borderRadius: '6px',
      color: theme.accent,
      fontSize: '12px',
      fontWeight: '600',
      flexShrink: 0,
    },
    // Modal styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px',
    },
    modal: {
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      width: '100%',
      maxWidth: '340px',
    },
    addModal: {
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      width: '100%',
      maxWidth: '400px',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px',
      borderBottom: `1px solid ${theme.border}`,
    },
    modalTitle: {
      color: theme.text,
      fontSize: '16px',
      fontWeight: '600',
      margin: 0,
    },
    modalClose: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      backgroundColor: 'transparent',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: theme.textSecondary,
    },
    modalContent: {
      padding: '16px',
    },
    addModalContent: {
      padding: '16px',
      flex: 1,
      overflow: 'auto',
    },
    modalText: {
      color: '#8a8a8a',
      fontSize: '14px',
      lineHeight: '1.5',
      margin: '0 0 12px 0',
    },
    modalWarning: {
      color: theme.error,
      fontSize: '13px',
      fontWeight: '500',
      margin: '0 0 20px 0',
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
    },
    cancelButton: {
      flex: 1,
      padding: '12px',
      backgroundColor: 'transparent',
      border: '1px solid #3a3a3a',
      borderRadius: '10px',
      color: '#8a8a8a',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    confirmButton: {
      flex: 1,
      padding: '12px',
      backgroundColor: theme.accent,
      border: 'none',
      borderRadius: '10px',
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    unfollowConfirmButton: {
      flex: 1,
      padding: '12px',
      backgroundColor: theme.error,
      border: 'none',
      borderRadius: '10px',
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    // Add modal styles
    addSearchRow: {
      display: 'flex',
      gap: '10px',
      marginBottom: '16px',
    },
    addSearchInputWrapper: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 16px',
      backgroundColor: theme.bg,
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      color: theme.textSecondary,
    },
    addSearchInput: {
      flex: 1,
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      color: theme.text,
      fontSize: '14px',
    },
    searchButton: {
      padding: '12px 20px',
      backgroundColor: theme.accent,
      border: 'none',
      borderRadius: '12px',
      color: theme.text,
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    searchResultsList: {
      maxHeight: '300px',
      overflow: 'auto',
    },
    noResults: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.textSecondary,
    },
    searchResultCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      backgroundColor: theme.bg,
      borderRadius: '10px',
      marginBottom: '8px',
    },
    followButton: {
      padding: '8px 16px',
      backgroundColor: theme.accent,
      border: 'none',
      borderRadius: '8px',
      color: theme.text,
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      flexShrink: 0,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            <BackIcon />
          </button>
          <h1 style={styles.headerTitle}>Following</h1>
          <div style={{ width: 40 }} />
          <div style={styles.headerBorder} />
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Select Mode Confirmation Modal */}
      {showSelectConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowSelectConfirm(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Remove Followers</h3>
              <button style={styles.modalClose} onClick={() => setShowSelectConfirm(false)}>
                <XIcon />
              </button>
            </div>
            <div style={styles.modalContent}>
              <p style={styles.modalText}>
                You're about to enter selection mode. Select the traders you want to unfollow, then confirm.
              </p>
              <p style={styles.modalWarning}>
                This action cannot be undone.
              </p>
              <div style={styles.modalButtons}>
                <button style={styles.cancelButton} onClick={() => setShowSelectConfirm(false)}>
                  Cancel
                </button>
                <button style={styles.confirmButton} onClick={confirmSelectMode}>
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unfollow Confirmation Modal */}
      {showConfirmModal && (
        <div style={styles.modalOverlay} onClick={() => setShowConfirmModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Unfollow {selectedUsers.size} Trader{selectedUsers.size > 1 ? 's' : ''}?</h3>
              <button style={styles.modalClose} onClick={() => setShowConfirmModal(false)}>
                <XIcon />
              </button>
            </div>
            <div style={styles.modalContent}>
              <p style={styles.modalText}>
                Are you sure you want to unfollow {selectedUsers.size} trader{selectedUsers.size > 1 ? 's' : ''}?
                You won't see their posts when filtering by "Traders I Follow".
              </p>
              <p style={styles.modalWarning}>
                This action cannot be undone.
              </p>
              <div style={styles.modalButtons}>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowConfirmModal(false)}
                  disabled={unfollowing}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.unfollowConfirmButton,
                    opacity: unfollowing ? 0.5 : 1,
                  }}
                  onClick={confirmUnfollow}
                  disabled={unfollowing}
                >
                  {unfollowing ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={closeAddModal}>
          <div style={styles.addModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Find Traders to Follow</h3>
              <button style={styles.modalClose} onClick={closeAddModal}>
                <XIcon />
              </button>
            </div>
            <div style={styles.addModalContent}>
              {/* Search Input */}
              <div style={styles.addSearchRow}>
                <div style={styles.addSearchInputWrapper}>
                  <SearchIcon />
                  <input
                    type="text"
                    placeholder="Search by username..."
                    value={addSearchQuery}
                    onChange={(e) => setAddSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                    style={styles.addSearchInput}
                    autoFocus
                  />
                </div>
                <button
                  style={{
                    ...styles.searchButton,
                    opacity: !addSearchQuery.trim() ? 0.5 : 1,
                  }}
                  onClick={handleSearchUsers}
                  disabled={!addSearchQuery.trim() || searchLoading}
                >
                  {searchLoading ? '...' : 'Search'}
                </button>
              </div>

              {/* Search Results */}
              <div style={styles.searchResultsList}>
                {searchResults.length === 0 && addSearchQuery && !searchLoading ? (
                  <div style={styles.noResults}>
                    <p>No users found</p>
                    <span>Try a different username</span>
                  </div>
                ) : (
                  searchResults.map(result => (
                    <div key={result.id} style={styles.searchResultCard}>
                      <div style={styles.avatar}>
                        {result.avatar_url ? (
                          <img src={result.avatar_url} alt="" style={styles.avatarImage} />
                        ) : (
                          <span style={styles.avatarInitial}>
                            {result.username?.[0]?.toUpperCase() || '?'}
                          </span>
                        )}
                      </div>
                      <div style={styles.userInfo}>
                        <span style={styles.username}>@{result.username}</span>
                        <span style={styles.userMeta}>
                          {result.location_city && result.location_state
                            ? `${result.location_city}, ${result.location_state}`
                            : 'No location set'}
                        </span>
                      </div>
                      <button
                        style={{
                          ...styles.followButton,
                          opacity: followingUser === result.id ? 0.5 : 1,
                        }}
                        onClick={() => handleFollowUser(result)}
                        disabled={followingUser === result.id}
                      >
                        {followingUser === result.id ? '...' : 'Follow'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Following ({following.length})</h1>
        <div style={styles.headerActions}>
          {!selectMode ? (
            <>
              <button style={styles.addButton} onClick={() => setShowAddModal(true)}>
                <PlusIcon />
              </button>
              <button
                style={{
                  ...styles.removeButton,
                  opacity: following.length === 0 ? 0.5 : 1,
                }}
                onClick={handleEnterSelectMode}
                disabled={following.length === 0}
              >
                Remove
              </button>
            </>
          ) : (
            <button style={styles.cancelSelectButton} onClick={cancelSelectMode}>
              Cancel
            </button>
          )}
        </div>
        <div style={styles.headerBorder} />
      </div>

      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <div style={styles.searchInputWrapper}>
          <SearchIcon />
          <input
            type="text"
            placeholder="Search your following..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button style={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <XIcon />
            </button>
          )}
        </div>
      </div>

      {/* Selection Bar - Only show in select mode */}
      {selectMode && (
        <div style={styles.selectionBar}>
          <span style={styles.selectionText}>
            {selectedUsers.size} selected
          </span>
          <button
            style={{
              ...styles.unfollowButton,
              opacity: selectedUsers.size === 0 ? 0.5 : 1,
            }}
            onClick={handleUnfollowSelected}
            disabled={selectedUsers.size === 0}
          >
            <TrashIcon />
            <span>Remove Selected</span>
          </button>
        </div>
      )}

      {/* Following List */}
      <div style={styles.content}>
        {filteredFollowing.length === 0 ? (
          <div style={styles.emptyState}>
            {following.length === 0 ? (
              <>
                <UserPlusIcon />
                <p style={styles.emptyTitle}>Not following anyone yet</p>
                <p style={styles.emptySubtext}>Tap the + button to find traders to follow</p>
              </>
            ) : (
              <>
                <p style={styles.emptyTitle}>No results</p>
                <p style={styles.emptySubtext}>Try a different search term</p>
              </>
            )}
          </div>
        ) : (
          filteredFollowing.map(follow => {
            const followUser = follow.users;
            const isSelected = selectedUsers.has(followUser?.id);

            return (
              <div
                key={follow.id}
                style={{
                  ...styles.userCard,
                  ...(selectMode && isSelected ? styles.userCardSelected : {}),
                }}
                onClick={() => {
                  if (selectMode) {
                    toggleUserSelection(followUser?.id);
                  } else if (followUser?.id) {
                    navigate(`/user/${followUser.id}/collection`);
                  }
                }}
              >
                {selectMode && (
                  <div style={{
                    ...styles.checkbox,
                    ...(isSelected ? styles.checkboxChecked : {}),
                  }}>
                    {isSelected && <CheckIcon />}
                  </div>
                )}

                <div style={styles.avatar}>
                  {followUser?.avatar_url ? (
                    <img src={followUser.avatar_url} alt="" style={styles.avatarImage} />
                  ) : (
                    <span style={styles.avatarInitial}>
                      {followUser?.username?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>

                <div style={styles.userInfo}>
                  <span style={styles.username}>@{followUser?.username || 'Unknown'}</span>
                  <span style={styles.userMeta}>
                    {followUser?.location_city && followUser?.location_state
                      ? `${followUser.location_city}, ${followUser.location_state}`
                      : 'No location set'}
                    {' - '}
                    Followed {formatTimeAgo(follow.created_at)}
                  </span>
                </div>

                <div style={styles.tradeLevel}>
                  Lv.{followUser?.trade_level || 1}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ManageFollowing;
